import type { ReactNode } from 'react';
import type { PublicOwnerBranding } from '@pay/shared';
import { formatOrgnr, oneLineAddress } from '../lib/format';

// Settes til dagens dato når teksten endres.
export const SIST_ENDRET = '9. oktober 2026';

/**
 * Selve salgsvilkårene for betaling med Vipps gjennom Pay. Selgeren er eieren av kravet, ikke Pay. Med eier fylles
 * navn, org.nr., adresse og e-post inn; uten eier gis den generelle varianten, der selgeren er «firmaet som står i
 * betalingskravet». Brukes på kundesiden /vilkar/:ownerId og under Salgsvilkår i Pay-admin, så det admin viser, er
 * nøyaktig det kunden ser.
 */
export function SalgsvilkarTekst({ owner }: { owner: PublicOwnerBranding | null }) {
  const epost = owner?.support_email ? (
    <a href={`mailto:${owner.support_email}`} className="text-brand hover:underline">
      {owner.support_email}
    </a>
  ) : null;

  return (
    <article className="bg-white rounded-lg shadow p-6 sm:p-8 text-slate-700 leading-relaxed">
      <h1 className="text-2xl font-semibold text-slate-900">Salgsvilkår</h1>
      <p className="text-slate-500 mt-1">
        {owner ? `${owner.name} – betaling med Vipps` : 'Betaling med Vipps'} · Sist endret {SIST_ENDRET}
      </p>

      <Avsnitt tittel="1. Om vilkårene">
        <p>
          Disse salgsvilkårene gjelder når du betaler et krav fra {owner ? owner.name : 'selgeren'} med
          Vipps, enten via en betalingslenke, et varsel i Vipps-appen eller en QR-kode. Kravet gjelder en
          vare eller tjeneste du har kjøpt av selgeren, for eksempel en reparasjon, service, deler eller et
          produkt.
        </p>
        <p>
          Avtalen består av disse vilkårene, opplysningene i betalingskravet og eventuell ordrebekreftelse,
          arbeidsordre eller faktura fra selgeren. Har du og selgeren avtalt noe annet, gjelder det foran
          disse vilkårene, så lenge det ikke gir deg dårligere rettigheter enn loven gir deg.
        </p>
        <p>
          Vilkårene er skrevet for forbrukerkjøp. Kjøper du som næringsdrivende, gjelder kjøpsloven og det
          dere har avtalt.
        </p>
      </Avsnitt>

      <Avsnitt tittel="2. Partene">
        {owner ? (
          <p>
            Selger er <strong>{owner.name}</strong>, org.nr. {formatOrgnr(owner.orgnr)}
            {owner.address ? `, ${oneLineAddress(owner.address)}` : ''}
            {epost && <>, e-post {epost}</>}.
          </p>
        ) : (
          <p>Selger er firmaet som står som mottaker i betalingskravet, på betalingssiden og i Vipps-appen.</p>
        )}
        <p>Kjøper er den som betaler kravet, omtalt som «du».</p>
        <p>
          Betalingsløsningen er en teknisk tjeneste selgeren bruker for å ta imot betaling. Leverandøren av
          betalingsløsningen er ikke part i kjøpet. Pengene går direkte fra deg til selgeren gjennom
          selgerens avtale med Vipps.
        </p>
      </Avsnitt>

      <Avsnitt tittel="3. Pris">
        <p>
          Beløpet i betalingskravet er totalprisen du skal betale, inkludert eventuell merverdiavgift og
          avtalte kostnader som frakt eller gebyrer. Du betaler ikke noe gebyr til Vipps eller til
          betalingsløsningen.
        </p>
      </Avsnitt>

      <Avsnitt tittel="4. Betaling">
        <p>
          Du betaler med Vipps. Beløpet belastes kortet eller kontoen du har valgt i Vipps når du godkjenner
          betalingen. Fordi kravet gjelder en vare eller tjeneste som allerede er levert eller avtalt, trekkes
          beløpet med en gang. Kortopplysningene dine håndteres av Vipps. Verken selgeren eller
          betalingsløsningen ser dem.
        </p>
        <p>
          En betalingslenke gjelder til tidspunktet som står i kravet. Er lenken utløpt, kan du be selgeren
          om en ny. Et varsel i Vipps-appen må godkjennes innen noen få minutter. Selgeren kan trekke tilbake
          et krav som ikke er betalt.
        </p>
        <p>
          Du får kvittering i Vipps-appen. Har selgeren e-postadressen din, får du også kvittering på e-post.
        </p>
      </Avsnitt>

      <Avsnitt tittel="5. Levering">
        <p>
          Varen eller tjenesten leveres slik du og selgeren har avtalt, for eksempel ved henting, ved arbeid
          hjemme hos deg eller ved forsendelse. Er arbeidet allerede utført, er betalingen oppgjøret for det.
        </p>
        <p>
          Blir leveringen forsinket, har du rettigheter etter forbrukerkjøpsloven (varer) og
          håndverkertjenesteloven (reparasjoner og andre tjenester på ting og fast eiendom).
        </p>
      </Avsnitt>

      <Avsnitt tittel="6. Angrerett">
        <p>
          Inngikk du avtalen om varen eller tjenesten på nett, på telefon eller utenfor selgerens faste
          forretningssted, for eksempel hjemme hos deg, har du som hovedregel 14 dagers angrerett etter
          angrerettloven. For varer løper fristen fra du mottok varen. For tjenester løper den fra avtalen ble
          inngått.
        </p>
        <p>
          Det er måten avtalen ble inngått på som avgjør, ikke måten du betaler på. Kjøpte du over disk,
          eller leverte du noe til reparasjon hos selgeren, har du ikke angrerett etter angrerettloven, selv
          om du betaler med lenke etterpå.
        </p>
        <p>Angreretten gjelder blant annet ikke for</p>
        <ul className="list-disc pl-5 space-y-1 mb-3">
          <li>
            tjenester som er levert fullt ut, når du ba om at arbeidet skulle starte før angrefristen gikk ut
            og bekreftet at angreretten da faller bort
          </li>
          <li>reparasjon eller vedlikehold du selv ba selgeren komme hjem til deg for å utføre som en hastesak</li>
          <li>varer som er laget etter dine mål eller ønsker, eller som er tydelig tilpasset deg</li>
        </ul>
        <p>
          Angrer du en tjeneste som du har bedt om å få startet i angrefristen, betaler du for den delen som
          allerede er levert.
        </p>
        <p>
          <strong>Slik angrer du:</strong> Gi selgeren en tydelig melding innen fristen, for eksempel på
          e-post{epost && <> til {epost}</>}. Du kan bruke standard angreskjema, men det er ikke nødvendig.
          Selgeren betaler tilbake senest 14 dager etter at meldingen er mottatt, til samme betalingsmiddel
          gjennom Vipps. For varer kan selgeren vente med tilbakebetalingen til varen er kommet i retur. Du
          dekker kostnaden ved å returnere varen, med mindre noe annet er avtalt.
        </p>
      </Avsnitt>

      <Avsnitt tittel="7. Reklamasjon ved feil og mangler">
        <p>
          Er det noe galt med varen eller tjenesten, må du si fra til selgeren innen rimelig tid etter at du
          oppdaget feilen. For varer er en melding innen to måneder alltid tidsnok. Reklamasjonsfristen for
          varer er to år, eller fem år for ting som er ment å vare vesentlig lenger, etter
          forbrukerkjøpsloven. For reparasjoner og andre tjenester gjelder fristene i
          håndverkertjenesteloven.
        </p>
        <p>
          Har varen eller tjenesten en mangel, kan du etter forholdene kreve retting, ny levering,
          prisavslag, heving eller erstatning. Det skal ikke koste deg noe å få rettet en mangel.
        </p>
      </Avsnitt>

      <Avsnitt tittel="8. Tilbakebetaling">
        <p>
          Skal du ha penger tilbake etter angrerett, heving eller prisavslag, eller fordi noe er belastet
          feil, betaler selgeren tilbake til samme betalingsmiddel gjennom Vipps. Det kan ta noen virkedager
          før beløpet vises på kontoen din.
        </p>
      </Avsnitt>

      <Avsnitt tittel="9. Personopplysninger">
        <p>
          Selgeren er behandlingsansvarlig for personopplysningene i betalingskravet. For å gjennomføre
          betalingen og sende kvittering behandles navnet ditt, mobilnummeret, e-postadressen (om du har
          oppgitt den), beløpet og hva kravet gjelder. Betalingsløsningen behandler opplysningene på vegne av
          selgeren. Vipps MobilePay AS får de opplysningene som trengs for å gjennomføre betalingen, og
          behandler dem etter sine egne vilkår.
        </p>
        <p>
          Opplysningene brukes til å gjennomføre betalingen, sende kvittering og føre regnskapet. De lagres
          ikke lenger enn nødvendig, men regnskapsopplysninger må oppbevares så lenge bokføringsloven krever.
        </p>
        <p>
          Du kan be selgeren om innsyn, retting eller sletting{epost && <> på {epost}</>}. Mener du at
          opplysningene behandles i strid med regelverket, kan du klage til Datatilsynet.
        </p>
      </Avsnitt>

      <Avsnitt tittel="10. Klage og tvister">
        <p>
          Ta først kontakt med selgeren{epost && <> på {epost}</>}, så prøver dere å finne en løsning. Gjelder
          klagen selve betalingen, for eksempel et beløp du ikke kjenner igjen, kan du også kontakte Vipps.
        </p>
        <p>
          Kommer dere ikke til enighet, kan du som forbruker få hjelp av{' '}
          <a href="https://www.forbrukerradet.no" target="_blank" rel="noopener" className="text-brand hover:underline">
            Forbrukerrådet
          </a>
          , som kan mekle mellom dere. Saken kan deretter bringes inn for Forbrukerklageutvalget. Du kan også
          bringe saken inn for forliksrådet eller domstolene.
        </p>
      </Avsnitt>

      <Avsnitt tittel="11. Kontakt">
        {owner ? (
          <address className="not-italic">
            <strong>{owner.name}</strong>
            <br />
            Org.nr. {formatOrgnr(owner.orgnr)}
            {owner.address && <div className="whitespace-pre-line">{owner.address}</div>}
            {epost && <div>E-post: {epost}</div>}
          </address>
        ) : (
          <p>Kontaktinformasjonen til selgeren står i betalingskravet, på betalingssiden og på kvitteringen.</p>
        )}
      </Avsnitt>
    </article>
  );
}

function Avsnitt({ tittel, children }: { tittel: string; children: ReactNode }) {
  return (
    <section className="mt-7 [&_p]:mb-3">
      <h2 className="text-lg font-semibold text-slate-900 mb-2">{tittel}</h2>
      {children}
    </section>
  );
}
